import * as React from "react";

import { Alert, Divider, styled } from "@mui/material";

import { UserContext } from "context/usercontext";
import InfoBox from "src/common/components/ui/InfoBox";
import useBridgeClient from "src/common/hooks/BridgeSWRClient";
import { TemplateSelect, useGetTemplates } from "src/domain/core/templates";
import { Launcher } from "src/domain/core/workflow";
import { endpoint } from "src/lib/bridgeClient";

const BoxCont = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

interface WorkflowLauncherProps {
  disabled: boolean;
  nodes: Nodes;
  instancesError: Error;
}

const LauncherDivider = styled(Divider)(({ theme }) => ({
  marginTop: 23,
  marginBottom: 23,
  fontSize: "14px",
  fontWeight: 600,
  color: theme.palette.text.secondary,
}));

export const WorkflowLauncher: React.FC<WorkflowLauncherProps> = ({ disabled, nodes, instancesError }) => {
  const { data: templates, error: templatesError } = useGetTemplates();
  const { data: blueprints, error } = useBridgeClient<WorkflowBlueprintDesc[]>(endpoint.stacks);
  const { data: disks, error: disksError } = useBridgeClient<Disk[]>(endpoint.getMyDisks, {
    refreshInterval: 15000,
  });

  return (
    <>
      {blueprints && (
        <>
          {templatesError ? (
            <Alert data-testid="template-alert" severity="warning">
              There was a problem retrieving templates for launch. Please try refreshing the page. If the problem
              persists, please contact support.
            </Alert>
          ) : (
            <TemplateSelect
              templates={templates}
              nodes={nodes}
              blueprints={blueprints}
              disabled={disabled || !!templatesError} //must have templates
              disks={disks}
              disksError={disksError}
            />
          )}
          <LauncherDivider>OR</LauncherDivider>
          <Launcher
            blueprints={blueprints}
            disabled={disabled}
            nodes={nodes}
            instancesError={instancesError}
            disks={disks}
            disksError={disksError}
            templates={templates}
          />
        </>
      )}
      {error && <div>Unable to get workflow blueprints</div>}
      <Divider sx={{ margin: "5px 0 5px 0" }} />
      <Info />
    </>
  );
};

const Info: React.FC = () => {
  const user = React.useContext(UserContext);

  return (
    <>
      <BoxCont>
        <InfoBox title={"Workflow Info"}>
          Workflows are launched with the following software pre-installed: R, Julia, Python, PsN, TexLive(LaTex),
          NONMEM Matlab, the Monolix Suite, Pirana, Posit Connect, Posit Workbench, VSCode, JupyterLab, JupyterNotebook,
          Git, Apache Subversion, DCV Remote desktop, SGE and Slurm
        </InfoBox>
      </BoxCont>
      <BoxCont>
        <InfoBox title={"MetrumRG Account Info"}>
          As part of your Metworx account, you can have up to {user.max_workflows} Workflows actively running at any
          given time
        </InfoBox>
      </BoxCont>
    </>
  );
};
